package pl.nickers.match3.ecs

import kotlin.random.Random

fun World.boardState(): BoardStateComponent? {
    val entities = getEntitiesForAspect(Aspect.all(BoardStateComponent::class))
    return entities.firstOrNull()?.let { getComponent(it, BoardStateComponent::class) }
}

fun World.boardSpawnControl(): BoardSpawnControlComponent? =
    boardEntityId()?.let { getComponent(it, BoardSpawnControlComponent::class) }

fun World.boardEntityId(): Int? =
    getEntitiesForAspect(Aspect.all(BoardStateComponent::class)).firstOrNull()

fun World.boardWidth(): Int = boardState()?.width ?: 0

fun World.boardHeight(): Int = boardState()?.height ?: 0

fun World.isHole(row: Int, col: Int): Boolean {
    val board = boardState() ?: return true
    if (row !in 0 until board.height || col !in 0 until board.width) return true
    return (row to col) in board.holes
}

fun World.findEntityAt(row: Int, col: Int): Int? {
    val posMapper = mapper<GridPositionComponent>()
    val aspect = Aspect.all(GridPositionComponent::class, JellyTypeComponent::class)
    return getEntitiesForAspect(aspect).firstOrNull {
        val pos = posMapper[it]!!
        pos.row == row && pos.col == col
    }
}

fun World.goalsComponent(): GoalsComponent? {
    val boardId = boardEntityId() ?: return null
    return getComponent(boardId, GoalsComponent::class)
}

/**
 * Returns the regular-jelly type with the highest count currently on the board, or
 * `null` if no regular jelly is present. "Regular jelly" means an entity whose
 * piece definition belongs to [Family.JELLY] — same exclusion as match detection.
 * Ties are broken by the smallest type id (deterministic).
 */
fun World.mostCommonJellyPieceId(pieceSet: PieceSet = PieceSetLoader.default()): String? {
    val counts = mutableMapOf<String, Int>()
    for (id in getEntitiesForAspect(Aspect.all(JellyTypeComponent::class, DraggableComponent::class))) {
        if (!isFamily(id, pieceSet, Family.JELLY)) continue
        val pieceId = pieceIdOf(id) ?: continue
        counts[pieceId] = (counts[pieceId] ?: 0) + 1
    }
    if (counts.isEmpty()) return null
    val maxCount = counts.values.max()
    // Deterministic tie-break: smallest piece id by natural string ordering.
    return counts.filterValues { it == maxCount }.keys.min()
}

/**
 * Counts, by piece id, how many of [entityIds] belong to [Family.JELLY]. Entities
 * outside the jelly family (specials, ice, spawn points) and entities without a
 * piece id are skipped. Cursed ingredients (E5) are skipped too: they clear and
 * score, but give no goal credit until cleansed.
 */
fun World.countPieceIds(
    entityIds: Set<Int>,
    pieceSet: PieceSet = PieceSetLoader.default(),
): Map<String, Int> {
    val result = mutableMapOf<String, Int>()
    for (id in entityIds) {
        if (!isFamily(id, pieceSet, Family.JELLY)) continue
        if (hasComponent(id, CursedComponent::class)) continue
        val pieceId = pieceIdOf(id) ?: continue
        result[pieceId] = (result[pieceId] ?: 0) + 1
    }
    return result
}

/** True if a Purified Rune terrain tile sits at ([row], [col]). */
fun World.hasPurifiedRuneAt(row: Int, col: Int): Boolean {
    val posMapper = mapper<GridPositionComponent>()
    return getEntitiesForAspect(
        Aspect.all(GridPositionComponent::class, PurifiedRuneComponent::class),
    ).any { posMapper[it]?.let { p -> p.row == row && p.col == col } == true }
}

/** True if a Void Puddle terrain tile sits at ([row], [col]). */
fun World.hasVoidPuddleAt(row: Int, col: Int): Boolean {
    val posMapper = mapper<GridPositionComponent>()
    return getEntitiesForAspect(
        Aspect.all(GridPositionComponent::class, VoidPuddleTerrainComponent::class),
    ).any { posMapper[it]?.let { p -> p.row == row && p.col == col } == true }
}

/**
 * E5 — Purified Rune cleanse: removes [CursedComponent] from every entity in
 * [entityIds] that stands on a rune cell. Must run **before** [countPieceIds] in the
 * match path so a cleansed ingredient regains goal credit for the very match that
 * cleanses it.
 */
fun World.cleanseCursedOnRunes(entityIds: Set<Int>) {
    val cursedMapper = mapper<CursedComponent>()
    val posMapper = mapper<GridPositionComponent>()
    for (id in entityIds) {
        if (!cursedMapper.has(id)) continue
        val pos = posMapper[id] ?: continue
        if (hasPurifiedRuneAt(pos.row, pos.col)) cursedMapper.remove(id)
    }
}

fun World.decrementGoals(pieceIdCounts: Map<String, Int>) {
    if (pieceIdCounts.isEmpty()) return
    val goalsComp = goalsComponent() ?: return
    var totalDecremented = 0
    for (goal in goalsComp.goals) {
        if (goal.completed) continue
        val matched = pieceIdCounts[goal.pieceId] ?: continue
        val previous = goal.remainingQuantity
        goal.remainingQuantity = (goal.remainingQuantity - matched).coerceAtLeast(0)
        if (goal.remainingQuantity == 0) goal.completed = true
        totalDecremented += previous - goal.remainingQuantity
    }
    val familiarEntity = getEntitiesForAspect(Aspect.all(FamiliarComponent::class)).firstOrNull() ?: return
    val mapper: ComponentMapper<FamiliarComponent> = mapper()
    val familiar = mapper[familiarEntity] ?: return
    // A Conjurer advances only on its trigger ingredient; the Black Cat (null trigger)
    // advances on every goal ingredient collected this clear.
    val gained = familiar.triggerPieceId?.let { pieceIdCounts[it] ?: 0 } ?: totalDecremented
    if (gained > 0) familiar.progress += gained
}

/**
 * Creates [FlyingSpriteComponent] entities for regular jellies in [entityIds] that still count toward an incomplete goal.
 * Must run **before** those entities are deleted or moved so their position/image data is still available.
 */
fun World.enqueueGoalFlightsForRemoval(
    entityIds: Set<Int>,
    pieceSet: PieceSet = PieceSetLoader.default(),
) {
    val goalsComp = goalsComponent() ?: return
    if (goalsComp.goals.isEmpty()) return
    val posMapper = mapper<GridPositionComponent>()
    val bodyMapper = mapper<BodyImageComponent>()
    val faceMapper = mapper<JellyFaceComponent>()
    for (id in entityIds) {
        if (!entityExists(id)) continue
        if (!isFamily(id, pieceSet, Family.JELLY)) continue
        // Cursed ingredients yield no goal credit, so no goal flight either.
        if (hasComponent(id, CursedComponent::class)) continue
        val pieceId = pieceIdOf(id) ?: continue
        val hasActiveGoal = goalsComp.goals.any { !it.completed && it.pieceId == pieceId }
        if (!hasActiveGoal) continue
        val pos = posMapper[id] ?: continue
        val body = bodyMapper[id]?.image ?: pieceSet.pieces[pieceId]?.sprites?.body ?: "jelly_1.png"
        val face = faceMapper[id]?.image ?: "face_1.png"
        val flightEntity = createEntity()
        addComponent(
            flightEntity,
            FlyingSpriteComponent(
                row = pos.row,
                col = pos.col,
                bodyImage = body,
                faceImage = face,
                goalPieceId = pieceId,
            ),
        )
    }
}

/**
 * Creates a [FamiliarFlightComponent] entity for a sprite that flies from the familiar
 * display to the ([row], [col]) cell of a jelly the familiar just transmuted. The
 * board-direction mirror of [enqueueGoalFlightsForRemoval].
 */
fun World.enqueueFamiliarFlight(row: Int, col: Int, bodyImage: String, faceImage: String) {
    val flightEntity = createEntity()
    addComponent(
        flightEntity,
        FamiliarFlightComponent(row = row, col = col, bodyImage = bodyImage, faceImage = faceImage),
    )
}

/**
 * Score awarded for removing [entityIds] as already-scorable match/area-effect
 * ingredients. Normal clears are worth 10; Purified Rune cells add +1.
 */
fun World.removalScore(entityIds: Set<Int>): Int {
    var total = 0
    val posMapper = mapper<GridPositionComponent>()
    for (id in entityIds) {
        total += 10
        val pos = posMapper[id]
        if (pos != null && hasPurifiedRuneAt(pos.row, pos.col)) {
            total += 1
        }
    }
    return total
}

fun World.decrementGoalsAndCheckWin(pieceIdCounts: Map<String, Int>, board: BoardStateComponent) {
    decrementGoals(pieceIdCounts)
    if (checkAllGoalsCompleted()) board.won = true
}

fun World.scoreDeleteAndCheckGoals(
    entitiesToRemove: Set<Int>,
    board: BoardStateComponent,
    pieceSet: PieceSet = PieceSetLoader.default(),
) {
    if (entitiesToRemove.isEmpty()) return
    val pieceIdCounts = countPieceIds(entitiesToRemove, pieceSet)
    // E5: cursed ingredients caught in an area effect still clear, but award no points —
    // only non-cursed items count (countPieceIds already withholds their goal credit).
    // Unlike a match, an area effect is scored per-item: clean items in the blast still count.
    val scorableEntities = entitiesToRemove
        .filterNot { hasComponent(it, CursedComponent::class) }
        .toSet()
    board.score += removalScore(scorableEntities)
    enqueueGoalFlightsForRemoval(entitiesToRemove, pieceSet)
    entitiesToRemove.forEach(::deleteEntity)
    decrementGoalsAndCheckWin(pieceIdCounts, board)
}

/**
 * True if any component that drives an on-screen animation exists in the world.
 * Used by the new 4-phase dispatcher to decide ANIMATE ↔ GRAVITY/RESOLVE transitions.
 */
fun World.anyAnimInFlight(): Boolean {
    if (getEntitiesForAspect(Aspect.all(SwappingComponent::class)).isNotEmpty()) return true
    if (getEntitiesForAspect(Aspect.all(FallingComponent::class)).isNotEmpty()) return true
    if (getEntitiesForAspect(Aspect.all(TeleportingComponent::class)).isNotEmpty()) return true
    if (getEntitiesForAspect(Aspect.all(ExplodingComponent::class)).isNotEmpty()) return true
    if (getEntitiesForAspect(Aspect.all(FusedBombComponent::class)).isNotEmpty()) return true
    if (getEntitiesForAspect(Aspect.all(ActivatedLaserComponent::class)).isNotEmpty()) return true
    if (getEntitiesForAspect(Aspect.all(SwirlVacuumComponent::class)).isNotEmpty()) return true
    return false
}

fun World.checkAllGoalsCompleted(): Boolean {
    val goalsComp = goalsComponent() ?: return false
    return goalsComp.goals.isNotEmpty() && goalsComp.goals.all { it.completed }
}

fun World.hasValidMove(width: Int, height: Int, pieceSet: PieceSet = PieceSetLoader.default()): Boolean {
    val draggableMapper = mapper<DraggableComponent>()
    fun isSpecial(entity: Int) = hasActivation(entity, pieceSet, Activation.DOUBLE_CLICK)
    for (row in 0 until height) {
        for (col in 0 until width) {
            val eA = findEntityAt(row, col) ?: continue
            if (!draggableMapper.has(eA)) continue
            val isSpecialA = isSpecial(eA)
            if (col + 1 < width) {
                val eB = findEntityAt(row, col + 1) ?: continue
                if (!draggableMapper.has(eB)) continue
                if (isSpecialA || isSpecial(eB) || swapProducesMatch(eA, eB)) return true
            }
            if (row + 1 < height) {
                val eB = findEntityAt(row + 1, col) ?: continue
                if (!draggableMapper.has(eB)) continue
                if (isSpecialA || isSpecial(eB) || swapProducesMatch(eA, eB)) return true
            }
        }
    }
    return false
}

/**
 * The first adjacent pair of regular (non-special) draggable jellies, in raster scan
 * order (row-major, left-to-right then top-to-bottom), whose swap produces a match —
 * i.e. a genuine teaching example of the core match-3 mechanic. Powers the level-1
 * tutorial's step-1 highlight ([TutorialComponent], issue #110); pure board inspection
 * via [swapProducesMatch], no side effects.
 *
 * Specials (bombs/lasers/swirls) are deliberately excluded even though swapping one
 * always counts as a "valid move" ([hasValidMove]): the tutorial teaches a 3-in-a-row
 * match, not a special-tile activation.
 *
 * Returns null when no such swap exists (empty/boardless state, or a board made up
 * entirely of specials/ice/holes) — callers must treat that as "no hint available" and
 * never crash or soft-lock on it.
 */
fun World.firstAvailableSwapHint(
    width: Int,
    height: Int,
    pieceSet: PieceSet = PieceSetLoader.default(),
): Pair<Pair<Int, Int>, Pair<Int, Int>>? {
    val draggableMapper = mapper<DraggableComponent>()
    fun plainJellyAt(row: Int, col: Int): Int? {
        val entity = findEntityAt(row, col) ?: return null
        if (!draggableMapper.has(entity)) return null
        if (hasActivation(entity, pieceSet, Activation.DOUBLE_CLICK)) return null
        return entity
    }
    for (row in 0 until height) {
        for (col in 0 until width) {
            val eA = plainJellyAt(row, col) ?: continue
            if (col + 1 < width) {
                val eB = plainJellyAt(row, col + 1)
                if (eB != null && swapProducesMatch(eA, eB)) return (row to col) to (row to (col + 1))
            }
            if (row + 1 < height) {
                val eB = plainJellyAt(row + 1, col)
                if (eB != null && swapProducesMatch(eA, eB)) return (row to col) to ((row + 1) to col)
            }
        }
    }
    return null
}

fun World.swapProducesMatch(eA: Int, eB: Int): Boolean {
    val typeMapper = mapper<JellyTypeComponent>()
    val board = boardState() ?: return false
    val typeA = typeMapper[eA]!!
    val typeB = typeMapper[eB]!!
    typeMapper.set(eA, typeB)
    typeMapper.set(eB, typeA)
    val found = findMatchesOnGrid(this, board.width, board.height).isNotEmpty()
    typeMapper.set(eA, typeA)
    typeMapper.set(eB, typeB)
    return found
}

fun World.neighborBodyImages(row: Int, col: Int, width: Int, height: Int): Set<String> {
    val bodyImageMapper = mapper<BodyImageComponent>()
    val images = mutableSetOf<String>()
    for ((dr, dc) in listOf(-1 to 0, 1 to 0, 0 to -1, 0 to 1)) {
        val nr = row + dr
        val nc = col + dc
        if (nr in 0 until height && nc in 0 until width) {
            findEntityAt(nr, nc)?.let { bodyImageMapper[it]?.let { c -> images.add(c.image) } }
        }
    }
    return images
}

fun World.eliminateInitialMatches(width: Int, height: Int, catalog: EntityCatalog, random: Random) {
    val posMapper = mapper<GridPositionComponent>()
    val typeMapper = mapper<JellyTypeComponent>()
    val bodyImageMapper = mapper<BodyImageComponent>()
    val pieceIdMapper = mapper<PieceIdComponent>()
    var matches = findMatchesOnGrid(this, width, height)
    while (matches.isNotEmpty()) {
        val entityId = matches.random()
        val pos = posMapper[entityId]!!
        val neighborImages = neighborBodyImages(pos.row, pos.col, width, height)
        val candidates = catalog.jellyBodyImages.filter { it !in neighborImages }
        if (candidates.isNotEmpty()) {
            val newBodyImage = candidates.random()
            val newTypeId = catalog.typeIdForBody(newBodyImage)
            typeMapper.set(entityId, JellyTypeComponent(newTypeId))
            bodyImageMapper.set(entityId, BodyImageComponent(newBodyImage))
            if (newTypeId in 1..6) {
                pieceIdMapper.set(entityId, PieceIdComponent(pieceIdForJellyType(newTypeId)))
            }
        }
        matches = findMatchesOnGrid(this, width, height)
    }
}
